// jgd-swym-tile-nudge — makes Swym's collections wishlist button pick up
// product tiles that an app renders client-side, one tile at a time (RCC
// grid: first render on collection pages, every "load more" batch).
// Selectors come from Swym's own config, so the theme settings stay the
// single source. See jgd-DEPENDENCIES.md for the mechanism.

(() => {
  const BUTTON_SELECTOR = '.swym-advanced-wishlist-collections-container';
  const DEBOUNCE_MS = 150;
  /** Swym debounces its observer (300ms) and keeps only the last batch of
   *  records, so a nudge can get lost while the grid is still rendering. */
  const RETRY_MS = 700;
  const MAX_ATTEMPTS = 3;

  /** Nudges per tile. Capped: tiles Swym refuses (disabled products) would
   *  otherwise loop forever. */
  const attempts = new WeakMap();
  let timer = 0;
  /** After a nudge, mutations (our own wrapper, Swym's buttons) are ignored
   *  until the retry check — more nudges would only reset Swym's debounce. */
  let quietUntil = 0;

  function selectors() {
    const config = window.SwymAdvancedCollectionsWishlistConfig;
    if (!config?.AttachButtonSelector || !config?.ProductUrlSelector) return null;
    return { tile: config.AttachButtonSelector, url: config.ProductUrlSelector };
  }

  /** A minimal, image-free copy of a tile that still matches both selectors. */
  function buildDecoy(tile, urlSelector) {
    const link = tile.querySelector(urlSelector);
    const decoy = tile.cloneNode(false);
    decoy.removeAttribute('id');
    decoy.appendChild(link.cloneNode(false));
    if (decoy.querySelector(urlSelector)) return decoy;

    // The url selector depends on wrappers in between — fall back to a deep
    // copy without media.
    const deep = tile.cloneNode(true);
    deep.removeAttribute('id');
    for (const media of deep.querySelectorAll('img, picture, video, source')) media.remove();
    return deep;
  }

  function check() {
    const sel = selectors();
    if (!sel) return;

    let pending = null;
    for (const tile of document.querySelectorAll(sel.tile)) {
      if (tile.querySelector(BUTTON_SELECTOR) || !tile.querySelector(sel.url)) continue;
      const count = attempts.get(tile) ?? 0;
      if (count >= MAX_ATTEMPTS) continue;
      attempts.set(tile, count + 1);
      pending ??= tile;
    }
    if (!pending) return;

    // Swym's observer only reacts to added nodes that *contain* a button-less
    // tile; it then re-scans the whole document. A hidden wrapper around a
    // decoy tile is exactly that trigger. The mutation record keeps the node,
    // so the wrapper leaves the DOM again at once — before Swym's re-scan
    // could decorate the decoy.
    const wrapper = document.createElement('div');
    wrapper.hidden = true;
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.appendChild(buildDecoy(pending, sel.url));
    document.body.appendChild(wrapper);
    wrapper.remove();

    quietUntil = performance.now() + RETRY_MS;
    setTimeout(check, RETRY_MS);
  }

  function schedule() {
    if (performance.now() < quietUntil) return;
    clearTimeout(timer);
    timer = setTimeout(check, DEBOUNCE_MS);
  }

  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  schedule();
})();
